<template>
  <VaDropdown
    ref="dropdown"
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge v-if="pendingCount > 0" overlap>
          <template #text>{{ pendingCount > 9 ? '9+' : pendingCount }}</template>
          <VaIcon name="report" class="notification-dropdown__icon" color="textPrimary" />
        </VaBadge>
        <VaIcon v-else name="report" class="notification-dropdown__icon" color="textPrimary" />
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[460px] sm:h-auto notification-content">
      <section class="sm:max-h-[400px] overflow-auto">
        <!-- Header -->
        <div class="notification-header">
          <div class="header-info">
            <VaIcon name="report" size="24px" color="warning" />
            <div class="header-text">
              <h3 class="header-title">Bekleyen İhlaller</h3>
              <p class="header-subtitle">Onay bekleyen {{ pendingCount }} ihlal raporu</p>
            </div>
          </div>
          <VaButton v-if="pendingViolations.length > 0" preset="secondary" size="small" @click="goToViolations">
            <VaIcon name="visibility" size="16px" class="mr-1" />
            Tümünü Gör
          </VaButton>
        </div>

        <!-- Stats Bar -->
        <div v-if="pendingViolations.length > 0" class="stats-bar">
          <div class="stat-item">
            <VaIcon name="schedule" size="16px" color="info" />
            <span class="stat-text">Bugün: {{ getTodayCount() }}</span>
          </div>
          <div class="stat-item">
            <VaIcon name="trending_up" size="16px" color="success" />
            <span class="stat-text">Bu hafta: {{ getWeekCount() }}</span>
          </div>
        </div>

        <div v-if="loading" class="text-center py-4">
          <VaProgressCircle indeterminate size="small" />
          <p class="text-sm text-secondary mt-2">Yükleniyor...</p>
        </div>

        <div v-else-if="pendingViolations.length === 0" class="text-center py-4">
          <VaIcon name="check_circle" size="48px" color="success" class="mb-2" />
          <p class="text-sm text-secondary">Bekleyen ihlal bulunmuyor</p>
        </div>

        <VaList v-else class="space-y-1 mb-2">
          <template v-for="(violation, index) in displayedViolations" :key="violation.id">
            <VaListItem class="text-base cursor-pointer" @click="goToViolation(violation.id)">
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon name="report" color="warning" />
              </VaListItemSection>
              <VaListItemSection>
                <div class="flex flex-col">
                  <span class="font-medium">{{ violation.licensePlate }}</span>
                  <span class="text-sm violation-type">{{ getTypeLabel(violation.violationType) }}</span>
                  <span class="text-xs violation-time">{{ formatDate(violation.reportedAt) }}</span>
                </div>
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator v-if="index !== displayedViolations.length - 1" class="mx-3" />
          </template>
        </VaList>

        <VaButton
          v-if="pendingViolations.length > displayLimit"
          preset="secondary"
          class="w-full"
          @click="showMore = !showMore"
        >
          {{ showMore ? 'Daha Az Göster' : `${pendingViolations.length - displayLimit} Daha Fazla` }}
        </VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { adminApi, type ViolationReport } from '../../../../services/api'

const router = useRouter()

const dropdown = ref()
const pendingViolations = ref<ViolationReport[]>([])
const loading = ref(false)
const showMore = ref(false)
const displayLimit = 5

const pendingCount = computed(() => pendingViolations.value.length)

const displayedViolations = computed(() => {
  return showMore.value ? pendingViolations.value : pendingViolations.value.slice(0, displayLimit)
})

// Enum definitions for mapping
enum ViolationType {
  emergency_lane_violation = 0,
  red_light_violation = 1,
  illegal_beacon_usage = 2,
  illegal_parking = 3,
  speed_violation = 4,
  other = 5,
}

const getTypeLabel = (type: string | number) => {
  // Handle both string and numeric enum values
  const typeString = typeof type === 'number' ? Object.keys(ViolationType)[type] : type
  const typeMap: Record<string, string> = {
    emergency_lane_violation: 'Acil Şerit İhlali',
    red_light_violation: 'Kırmızı Işık İhlali',
    illegal_beacon_usage: 'Yasadışı Flaşör Kullanımı',
    illegal_parking: 'Yasadışı Park',
    speed_violation: 'Hız İhlali',
    other: 'Diğer',
    '0': 'Acil Şerit İhlali',
    '1': 'Kırmızı Işık İhlali',
    '2': 'Yasadışı Flaşör Kullanımı',
    '3': 'Yasadışı Park',
    '4': 'Hız İhlali',
    '5': 'Diğer',
  }
  return typeMap[typeString] || typeString
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) return 'Şimdi'
  if (diffInMinutes < 60) return `${diffInMinutes} dk önce`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours} saat önce`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays} gün önce`

  return date.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const fetchPendingViolations = async () => {
  try {
    loading.value = true
    const response = await adminApi.getViolations({
      status: 'pending',
      pageSize: 20,
    })
    pendingViolations.value = response.violations
  } catch (error) {
    console.error('Error fetching pending violations:', error)
  } finally {
    loading.value = false
  }
}

const goToViolations = () => {
  router.push({ name: 'violations', query: { status: 'pending' } })
  // Close the dropdown after navigation
  if (dropdown.value) {
    dropdown.value.hide()
  }
}

const goToViolation = (id: string) => {
  router.push({ name: 'violation-detail', params: { id } })
  // Close the dropdown after navigation
  if (dropdown.value) {
    dropdown.value.hide()
  }
}

const getTodayCount = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return pendingViolations.value.filter((violation) => {
    const violationDate = new Date(violation.reportedAt)
    violationDate.setHours(0, 0, 0, 0)
    return violationDate.getTime() === today.getTime()
  }).length
}

const getWeekCount = () => {
  const weekAgo = new Date()
  weekAgo.setDate(weekAgo.getDate() - 7)
  return pendingViolations.value.filter((violation) => {
    const violationDate = new Date(violation.reportedAt)
    return violationDate >= weekAgo
  }).length
}

onMounted(() => {
  fetchPendingViolations()
})

// Refresh data every 5 minutes
setInterval(
  () => {
    fetchPendingViolations()
  },
  5 * 60 * 1000,
)
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
    color: var(--va-text-primary) !important;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }

  .va-button {
    background: transparent !important;
    border: 1px solid var(--va-background-border) !important;

    &:hover {
      background: var(--va-background-element) !important;
      border-color: var(--va-primary) !important;
    }
  }
}

.notification-content {
  background: var(--va-background-secondary);
  border: 1px solid var(--va-background-border);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--va-background-border);
  background: var(--va-background-element);
  border-radius: 12px 12px 0 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.header-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--va-text-primary);
  margin: 0;
}

.header-subtitle {
  font-size: 0.8rem;
  color: var(--va-text-secondary);
  margin: 0;
}

.stats-bar {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  background: var(--va-background-element);
  border-bottom: 1px solid var(--va-background-border);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stat-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--va-text-secondary);
}

:deep(.va-list-item) {
  transition: all 0.2s ease;
  border-radius: 8px;
  margin: 0.25rem 0.75rem;
  padding: 0.75rem;
  background: var(--va-background-element);
  border: 1px solid transparent;
}

:deep(.va-list-item:hover) {
  background: var(--va-background-border);
  border-color: var(--va-primary);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(var(--va-primary-rgb), 0.1);
}

:deep(.va-list-item-section) {
  padding: 0.25rem 0;
}

:deep(.va-list-separator) {
  margin: 0.5rem 1rem;
  background: linear-gradient(90deg, transparent, var(--va-background-border), transparent);
}

.violation-type {
  color: var(--va-text-primary);
  opacity: 0.9;
  font-weight: 500;
}

.violation-time {
  color: var(--va-text-primary);
  opacity: 0.7;
  font-weight: 400;
}
</style>
